import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import PageHeader from "../components/PageHeader";
import Card from "../components/Card";
import DataTable from "../components/DataTable";
import StatesMultiSelect from "../components/StatesMultiSelect";
import {
  fetchProviders,
  fetchStates,
  createProvider,
  updateProvider,
  deleteProvider,
  assignProviderStates
} from "../services/api";
import { checkPinValidity } from "../utils/pinAuth";
import { usePinInput, PinInputGroup } from "../components/PinInput";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { 
    opacity: 0;
    transform: translate(-50%, -48%) scale(0.96);
  }
  to { 
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
`;

const PinOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: ${fadeIn} 0.3s ease;
`;

const PinModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: linear-gradient(145deg, #1a1a2e 0%, #16213e 100%);
  border-radius: 24px;
  padding: 3rem 3.5rem;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.5), 
              0 0 0 1px rgba(255, 255, 255, 0.1);
  text-align: center;
  animation: ${slideUp} 0.4s ease;
  min-width: 380px;

  &.shake {
    animation: ${shake} 0.5s ease;
  }
`;

const LockIcon = styled.div`
  width: 72px;
  height: 72px;
  margin: 0 auto 1.5rem;
  background: ${props => props.locked 
    ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' 
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${props => props.locked 
    ? '0 8px 32px rgba(239, 68, 68, 0.4)' 
    : '0 8px 32px rgba(102, 126, 234, 0.4)'};

  svg {
    width: 32px;
    height: 32px;
    color: white;
  }
`;

const PinTitle = styled.h2`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.5rem;
  letter-spacing: -0.02em;
`;

const PinSubtitle = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 2rem;
`;


const LockedMessage = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
  line-height: 1.6;
  margin-top: 1rem;

  strong {
    color: #ff6b6b;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease;
`;

const Modal = styled.div`
  background-color: var(--card-bg);
  border-radius: 16px;
  padding: 2rem;
  width: 90%;
  max-width: 600px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  border: 1px solid var(--border-color);
  animation: ${slideUp} 0.3s ease;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color);

  h2 {
    font-size: 1.35rem;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px;
  line-height: 1;
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    background: var(--card-highlight-bg);
    color: var(--text-primary);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 0.95rem;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(99, 91, 255, 0.15);
  }

  &::placeholder {
    color: var(--text-secondary);
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 0.95rem;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(99, 91, 255, 0.15);
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

// States selection UI is handled by `StatesMultiSelect` (fast search + click-to-toggle)

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  &.primary {
    background: linear-gradient(135deg, var(--primary-color) 0%, #764ba2 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(99, 91, 255, 0.3);

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(99, 91, 255, 0.4);
    }
  }

  &.secondary {
    background: var(--card-highlight-bg);
    color: var(--text-primary);
    border: 1px solid var(--border-color);

    &:hover {
      background: var(--border-color);
    }
  }

  &.danger {
    background: linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%);
    color: white;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(255, 107, 107, 0.4);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const TableControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchBar = styled.div`
  flex: 1;
  min-width: 250px;

  input {
    width: 100%;
    padding: 0.75rem 1rem;
    border-radius: 10px;
    border: 1px solid var(--border-color);
    background-color: var(--card-bg);
    color: var(--text-primary);
    font-size: 0.95rem;
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 3px rgba(99, 91, 255, 0.15);
    }

    &::placeholder {
      color: var(--text-secondary);
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0.65rem 1rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  white-space: nowrap;

  svg {
    width: 18px;
    height: 18px;
  }

  &.primary {
    background: linear-gradient(135deg, var(--primary-color) 0%, #764ba2 100%);
    color: white;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(99, 91, 255, 0.3);
    }
  }

&:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;

  &.active {
    background: rgba(16, 185, 129, 0.15);
    color: #10b981;
  }

  &.inactive {
    background: rgba(239, 68, 68, 0.15);
    color: #ef4444;
  }
`;

const StateBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  background: var(--card-highlight-bg);
  color: var(--text-secondary);
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  margin-right: 4px;
  margin-bottom: 4px;
`;

const StatesCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  max-width: 200px;
`;

const ActionCell = styled.div`
  display: flex;
  gap: 8px;
`;

const TableActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.2s;

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  &.edit {
    background: rgba(99, 91, 255, 0.1);
    color: #635bff;
    border-color: rgba(99, 91, 255, 0.2);

    &:hover {
      background: rgba(99, 91, 255, 0.2);
      border-color: rgba(99, 91, 255, 0.4);
    }
  }

  &.delete {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
    border-color: rgba(239, 68, 68, 0.2);

    &:hover {
      background: rgba(239, 68, 68, 0.2);
      border-color: rgba(239, 68, 68, 0.4);
    }
  }
`;

const RowNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: var(--card-highlight-bg);
  color: var(--text-secondary);
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  padding: 1.25rem;
  border-top: 1px solid var(--border-color);
  background-color: var(--card-highlight-bg);
  border-radius: 0 0 var(--border-radius) var(--border-radius);

  .pagination-info {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-secondary);
  }

  .pagination-controls {
    display: flex;
    gap: 0.5rem;
  }
`;

const PaginationButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0.25rem 0.5rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background-color: ${props => props.active ? "var(--primary-color)" : "var(--card-bg)"};
  color: ${props => props.active ? "white" : "var(--text-primary)"};
  font-weight: ${props => props.active ? "600" : "500"};
  cursor: ${props => props.disabled ? "not-allowed" : "pointer"};
  opacity: ${props => props.disabled ? "0.5" : "1"};
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: ${props => props.active ? "var(--primary-dark)" : "var(--card-highlight-bg)"};
    border-color: var(--primary-color);
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  padding: 3rem;

  .spinner {
    width: 44px;
    height: 44px;
    border: 3px solid var(--border-color);
    border-top-color: var(--primary-color);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ConfirmModal = styled.div`
  background-color: var(--card-bg);
  border-radius: 16px;
  padding: 2rem;
  width: 90%;
  max-width: 420px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  border: 1px solid var(--border-color);
  text-align: center;

  .icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 1.5rem;
    background: rgba(239, 68, 68, 0.1);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 32px;
      height: 32px;
      color: #ef4444;
    }
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 0.75rem;
  }

  p {
    color: var(--text-secondary);
    font-size: 0.95rem;
    margin-bottom: 1.5rem;
  }

  .actions {
    display: flex;
    gap: 1rem;
    justify-content: center;
  }
`;

const Toast = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  font-weight: 500;
  z-index: 9999;
  animation: ${fadeIn} 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);

  &.success {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
  }

  &.error {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
  }
`;

function ProvidersPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => checkPinValidity());
  const {
    pin, pinError, shake: shakeModal, attempts, locked: isLocked,
    inputRefs: pinInputs, handleChange: handlePinChange, handleKeyDown: handlePinKeyDown,
  } = usePinInput({ onSuccess: () => setIsAuthenticated(true), autoSubmit: true });

  const [providersData, setProvidersData] = useState({
    providers: [],
    count: 0,
    next: null,
    previous: null,
    currentPage: 1
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [providerSort, setProviderSort] = useState("provider_id"); // default ascending by backend id
  const pageSize = 10;

  const [states, setStates] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    credential: "MD",
    npi: "",
    dea_number: "",
    state_license_number: "",
    email: "",
    provider_type: "practitioner",
    is_active: true,
    is_global: true,
    state_ids: []
  });
  // state selection handled by StatesMultiSelect

  useEffect(() => {
    if (isAuthenticated) {
      loadProviders();
      loadStates();
    }
  }, [isAuthenticated, page, searchTerm, providerSort]);

  const loadProviders = async () => {
    try {
      setLoading(true);
      const data = await fetchProviders({
        page,
        search: searchTerm,
        ordering: providerSort,
        is_global: true
      });
      // Client-side safety sort by provider_id so the # column always aligns
      const sortedProviders = [...(data.providers || [])].sort((a, b) => {
        const dir = providerSort?.startsWith("-") ? -1 : 1;
        const aId = a?.provider_id ?? 0;
        const bId = b?.provider_id ?? 0;
        return (aId - bId) * dir;
      });
      setProvidersData({
        ...data,
        providers: sortedProviders
      });
    } catch (error) {
      showToast("Failed to load providers", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadStates = async () => {
    try {
      const statesData = await fetchStates();
      setStates(statesData);
    } catch (error) {
      console.error("Failed to load states:", error);
    }
  };


  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const resetForm = () => {
    setFormData({
      first_name: "",
      last_name: "",
      credential: "MD",
      npi: "",
      dea_number: "",
      state_license_number: "",
      email: "",
      provider_type: "practitioner",
      is_active: true,
      is_global: true,
      state_ids: []
    });
  };

  const handleAddProvider = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleEditProvider = (provider) => {
    setSelectedProvider(provider);
    setFormData({
      first_name: provider.first_name || "",
      last_name: provider.last_name || "",
      credential: provider.credential || "MD",
      npi: provider.npi || "",
      dea_number: provider.dea_number || "",
      state_license_number: provider.state_license_number || "",
      email: provider.email || "",
      provider_type: provider.provider_type || "practitioner",
      is_active: provider.is_active ?? true,
      is_global: provider.is_global ?? false,
      state_ids: provider.licensed_states?.map(s => s.state_id) || []
    });
    setShowEditModal(true);
  };

  const handleDeleteClick = (provider) => {
    setSelectedProvider(provider);
    setShowDeleteModal(true);
  };

  const handleStatesChange = (nextStateIds) => {
    setFormData((prev) => ({
      ...prev,
      state_ids: nextStateIds
    }));
  };

  const handleSaveProvider = async () => {
    try {
      setSaving(true);
      if (showEditModal && selectedProvider) {
        await updateProvider(selectedProvider.provider_id, formData);
        // Always replace all states (including clearing all)
        await assignProviderStates(selectedProvider.provider_id, formData.state_ids);
        showToast("Provider updated successfully");
      } else {
        await createProvider(formData);
        showToast("Provider created successfully");
      }
      setShowAddModal(false);
      setShowEditModal(false);
      loadProviders();
    } catch (error) {
      showToast(error.message || "Failed to save provider", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setSaving(true);
      await deleteProvider(selectedProvider.provider_id);
      showToast("Provider deleted successfully");
      setShowDeleteModal(false);
      loadProviders();
    } catch (error) {
      showToast(error.message || "Failed to delete provider", "error");
    } finally {
      setSaving(false);
    }
  };


  // StatesMultiSelect handles display of state name/abbr; this page only stores IDs.

  const totalPages = Math.ceil(providersData.count / pageSize);

  const columns = [
    {
      header: "#",
      accessor: "row_number",
      render: (_row, index) => (
        <RowNumber>{(page - 1) * pageSize + index + 1}</RowNumber>
      )
    },
    {
      header: "Name",
      accessor: "display_name",
      render: (row) => (
        <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>
          {row.display_name || `${row.first_name} ${row.last_name}`}
        </span>
      )
    },
    {
      header: "Credential",
      accessor: "credential"
    },
    {
      header: "NPI",
      accessor: "npi",
      render: (row) => (
        <span style={{ fontFamily: "monospace", fontSize: "0.9rem" }}>{row.npi}</span>
      )
    },
    {
      header: "Email",
      accessor: "email"
    },
    {
      header: "Licensed States",
      accessor: "licensed_states",
      render: (row) => (
        <StatesCell>
          {row.licensed_states?.slice(0, 5).map(state => (
            <StateBadge key={state.state_id}>{state.state_abbreviation}</StateBadge>
          ))}
          {row.licensed_states?.length > 5 && (
            <StateBadge>+{row.licensed_states.length - 5}</StateBadge>
          )}
          {(!row.licensed_states || row.licensed_states.length === 0) && (
            <span style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>None</span>
          )}
        </StatesCell>
      )
    },
    {
      header: "Status",
      accessor: "is_active",
      render: (row) => (
        <Badge className={row.is_active ? "active" : "inactive"}>
          {row.is_active ? "Active" : "Inactive"}
        </Badge>
      )
    },
    {
      header: "Actions",
      accessor: "provider_id",
      render: (row) => (
        <ActionCell>
          <TableActionButton
            className="edit"
            onClick={() => handleEditProvider(row)}
            title="Edit provider"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </TableActionButton>
          <TableActionButton
            className="delete"
            onClick={() => handleDeleteClick(row)}
            title="Delete provider"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </TableActionButton>
        </ActionCell>
      )
    }
  ];

  if (!isAuthenticated) {
    return (
      <PinOverlay>
        <PinModal className={shakeModal ? "shake" : ""}>
          <LockIcon locked={isLocked}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </LockIcon>
          
          {isLocked ? (
            <>
              <PinTitle>Access Locked</PinTitle>
              <PinSubtitle>Too many failed attempts</PinSubtitle>
              <LockedMessage>
                You have exceeded the maximum number of PIN attempts.<br />
                Please <strong>logout and login again</strong> to unlock access.
              </LockedMessage>
            </>
          ) : (
            <>
              <PinTitle>Provider Access</PinTitle>
              <PinSubtitle>Enter 4-digit PIN to continue</PinSubtitle>
              <PinInputGroup
                pin={pin}
                onChange={(i, val) => handlePinChange(i, val)}
                onKeyDown={(i, e) => handlePinKeyDown(i, e)}
                inputRefs={pinInputs}
                pinError={pinError}
                attempts={attempts}
              />
            </>
          )}
        </PinModal>
      </PinOverlay>
    );
  }

  return (
    <div>
      <PageHeader
        title="Global Providers"
        description="Manage healthcare providers and their licensed states."
      />

      <Card>
        <TableControls>
          <SearchBar>
            <input
              type="text"
              placeholder="Search by name, NPI, email, or credential..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
            />
          </SearchBar>

          <ActionButtons>
            <IconButton
              className="primary"
              onClick={() =>
                setProviderSort((prev) =>
                  prev === "provider_id" ? "-provider_id" : "provider_id"
                )
              }
              title="Toggle sort by provider id"
            >
              #
              <span style={{ fontSize: "0.8rem", opacity: 0.9 }}>
                {providerSort === "provider_id" ? "↑" : "↓"}
              </span>
            </IconButton>

            <IconButton className="primary" onClick={handleAddProvider}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add Provider
            </IconButton>
          </ActionButtons>
        </TableControls>

        {loading ? (
          <LoadingSpinner>
            <div className="spinner" />
          </LoadingSpinner>
        ) : (
          <>
            <DataTable
              columns={columns}
              data={providersData.providers}
              emptyMessage="No providers found matching your criteria."
            />

            {totalPages > 1 && (
              <Pagination>
                <div className="pagination-info">
                  Showing {Math.min(pageSize * (page - 1) + 1, providersData.count)} to{" "}
                  {Math.min(pageSize * page, providersData.count)} of {providersData.count} providers
                </div>

                <div className="pagination-controls">
                  <PaginationButton
                    onClick={() => setPage(1)}
                    disabled={page === 1}
                  >
                    «
                  </PaginationButton>
                  <PaginationButton
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                  >
                    ‹
                  </PaginationButton>

                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    return (
                      <PaginationButton
                        key={pageNum}
                        active={page === pageNum}
                        onClick={() => setPage(pageNum)}
                      >
                        {pageNum}
                      </PaginationButton>
                    );
                  })}

                  <PaginationButton
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                  >
                    ›
                  </PaginationButton>
                  <PaginationButton
                    onClick={() => setPage(totalPages)}
                    disabled={page === totalPages}
                  >
                    »
                  </PaginationButton>
                </div>
              </Pagination>
            )}
          </>
        )}
      </Card>

      {/* Add/Edit Provider Modal */}
      {(showAddModal || showEditModal) && (
        <ModalOverlay onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>{showEditModal ? "Edit Provider" : "Add New Provider"}</h2>
              <CloseButton onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
                ×
              </CloseButton>
            </ModalHeader>

            <FormRow>
              <FormGroup>
                <FormLabel>First Name *</FormLabel>
                <FormInput
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="Enter first name"
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>Last Name *</FormLabel>
                <FormInput
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Enter last name"
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <FormLabel>Credential</FormLabel>
                <FormSelect
                  value={formData.credential}
                  onChange={(e) => setFormData({ ...formData, credential: e.target.value })}
                >
                  <option value="MD">MD</option>
                  <option value="DO">DO</option>
                  <option value="NP">NP</option>
                  <option value="FNP-C">FNP-C</option>
                  <option value="PA">PA</option>
                  <option value="PA-C">PA-C</option>
                  <option value="DNP">DNP</option>
                  <option value="PhD">PhD</option>
                </FormSelect>
              </FormGroup>
              <FormGroup>
                <FormLabel>NPI *</FormLabel>
                <FormInput
                  type="text"
                  value={formData.npi}
                  onChange={(e) => setFormData({ ...formData, npi: e.target.value })}
                  placeholder="10-digit NPI"
                  maxLength={10}
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <FormLabel>DEA Number</FormLabel>
                <FormInput
                  type="text"
                  value={formData.dea_number}
                  onChange={(e) => setFormData({ ...formData, dea_number: e.target.value })}
                  placeholder="DEA number"
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>State License Number</FormLabel>
                <FormInput
                  type="text"
                  value={formData.state_license_number}
                  onChange={(e) => setFormData({ ...formData, state_license_number: e.target.value })}
                  placeholder="License number"
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <FormLabel>Email *</FormLabel>
              <FormInput
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="provider@example.com"
              />
            </FormGroup>

            <FormRow>
              <FormGroup>
                <FormLabel>Provider Type</FormLabel>
                <FormSelect
                  value={formData.provider_type}
                  onChange={(e) => setFormData({ ...formData, provider_type: e.target.value })}
                >
                  <option value="practitioner">Practitioner</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="collaborating">Collaborating</option>
                </FormSelect>
              </FormGroup>
              <FormGroup>
                <FormLabel>Status</FormLabel>
                <FormSelect
                  value={formData.is_active ? "active" : "inactive"}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.value === "active" })}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </FormSelect>
              </FormGroup>
            </FormRow>

            <FormGroup>
              <FormLabel>Licensed States</FormLabel>
              <StatesMultiSelect
                states={states}
                selectedIds={formData.state_ids}
                onChange={handleStatesChange}
              />
            </FormGroup>

            <ModalActions>
              <Button
                className="secondary"
                onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
              >
                Cancel
              </Button>
              <Button
                className="primary"
                onClick={handleSaveProvider}
                disabled={saving || !formData.first_name || !formData.last_name || !formData.npi || !formData.email}
              >
                {saving ? "Saving..." : (showEditModal ? "Update Provider" : "Create Provider")}
              </Button>
            </ModalActions>
          </Modal>
        </ModalOverlay>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <ModalOverlay onClick={() => setShowDeleteModal(false)}>
          <ConfirmModal onClick={(e) => e.stopPropagation()}>
            <div className="icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
            </div>
            <h3>Delete Provider?</h3>
            <p>
              Are you sure you want to delete <strong>{selectedProvider?.display_name || `${selectedProvider?.first_name} ${selectedProvider?.last_name}`}</strong>? 
              This action cannot be undone.
            </p>
            <div className="actions">
              <Button className="secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </Button>
              <Button className="danger" onClick={handleConfirmDelete} disabled={saving}>
                {saving ? "Deleting..." : "Delete Provider"}
              </Button>
            </div>
          </ConfirmModal>
        </ModalOverlay>
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast className={toast.type}>
          {toast.type === "success" ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          )}
          {toast.message}
        </Toast>
      )}
    </div>
  );
}

export default ProvidersPage;